/**
 * ReportDetailCard.tsx
 *
 * Renders the `report_detail` envelope data emitted by onething_get_report.
 * Shows header (date, user, priorityCode badge, blocker badge + reason),
 * then full content block.
 */

import { AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

// ---------------------------------------------------------------------------
// Data shape emitted by the BE envelope for kind "report_detail"
// ---------------------------------------------------------------------------

export interface ReportDetailData {
  pid: string;
  reportDate: string;
  userName: string;
  priorityCode: string;
  blocker: boolean;
  blockedReason?: string;
  content: string;
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

interface ReportDetailCardProps {
  data: ReportDetailData;
}

export function ReportDetailCard({ data }: ReportDetailCardProps) {
  return (
    <Card className="my-2 rounded-xl border-border/60 shadow-sm dark:shadow-none dark:ring-1 dark:ring-border/40 overflow-hidden">
      <CardHeader className="px-4 pt-3.5 pb-2">
        {/* Meta row */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-mono text-[10.5px] font-semibold text-muted-foreground">{data.reportDate}</span>
          <span className="text-[13px] font-semibold tracking-tight">{data.userName}</span>
          <Badge variant="outline" className="rounded-full font-mono text-[10.5px] px-2 py-0.5">
            {data.priorityCode}
          </Badge>
          {data.blocker && (
            <Badge
              variant="outline"
              className="gap-1 rounded-full text-[10.5px] font-semibold px-2 py-0.5 bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800"
            >
              <AlertTriangle className="h-2.5 w-2.5" />
              Blocker
            </Badge>
          )}
        </div>
        {/* Blocked reason */}
        {data.blocker && data.blockedReason && (
          <div className="mt-1.5 flex items-start gap-1.5 rounded-md bg-red-50 dark:bg-red-950/60 px-2 py-1 text-[10.5px] text-red-700 dark:text-red-300 leading-relaxed">
            <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
            <span>{data.blockedReason}</span>
          </div>
        )}
      </CardHeader>
      <CardContent className="px-4 pb-3.5 pt-0">
        <p className="text-[12.5px] text-foreground leading-relaxed whitespace-pre-wrap">
          {data.content}
        </p>
      </CardContent>
    </Card>
  );
}
